import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "../../../../app/store";
import { EventPayload } from "../../../../common/types/Event";
import {
  CreateTaxWithholdingCorrection,
  DeleteTaxWithholdingCorrection,
  TalentTaxWithholdingCorrection,
  TaxWithholdingCommentUpdated,
  TaxWithholdingCorrectionAmountUpdated,
  TaxWithholdingCorrectionCreated,
  TaxWithholdingCorrectionDateUpdated,
  TaxWithholdingCorrectionDeleted,
  TaxWithholdingCorrectionEvent,
  TaxWithholdingUpdateEvent,
  UpdateTaxWithholdingCorrection,
} from "./types";
import {
  createTaxWithholdingCorrection,
  deleteTaxWithholdingCorrection,
  getTalentTaxWithholdingCorrections,
  updateTaxWithholdingCorrection,
} from "./taxWithholdingCorrectionsAPI";

const taxWithholdingCorrectionsAdapter = createEntityAdapter<
  TalentTaxWithholdingCorrection,
  TalentTaxWithholdingCorrection["id"]
>({
  selectId: (taxWithholdingCorrection) => taxWithholdingCorrection.id,
});

export type SliceState = EntityState<
  TalentTaxWithholdingCorrection,
  TalentTaxWithholdingCorrection["id"]
> & {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
};

const initialState: SliceState =
  taxWithholdingCorrectionsAdapter.getInitialState({
    status: "idle",
    error: null,
  });

export const fetchTalentTaxWithholdingCorrections = createAsyncThunk<
  TalentTaxWithholdingCorrection[],
  string,
  { state: RootState }
>(
  "taxWithholdingCorrections/fetchTalentTaxWithholdingCorrections",
  async (
    talentId: string,
    { requestId },
  ): Promise<TalentTaxWithholdingCorrection[]> => {
    const res = await getTalentTaxWithholdingCorrections(talentId, {
      requestId,
    });
    return res.data;
  },
);

export const createTalentTaxWithholdingCorrection = createAsyncThunk<
  EventPayload<TaxWithholdingCorrectionCreated>[],
  CreateTaxWithholdingCorrection,
  { state: RootState }
>(
  "taxWithholdingCorrections/createTalentTaxWithholdingCorrection",
  async (
    taxWithholdingCorrection: CreateTaxWithholdingCorrection,
    { requestId },
  ) => {
    return await createTaxWithholdingCorrection(taxWithholdingCorrection, {
      requestId,
    });
  },
);

export const updateTalentTaxWithholdingCorrection = createAsyncThunk<
  EventPayload<TaxWithholdingUpdateEvent>[],
  UpdateTaxWithholdingCorrection,
  { state: RootState }
>(
  "taxWithholdingCorrections/updateTalentTaxWithholdingCorrection",
  async (
    taxWithholdingCorrection: UpdateTaxWithholdingCorrection,
    { requestId },
  ) => {
    return await updateTaxWithholdingCorrection(taxWithholdingCorrection, {
      requestId,
    });
  },
);

export const deleteTalentTaxWithholdingCorrection = createAsyncThunk<
  EventPayload<TaxWithholdingCorrectionDeleted>[],
  DeleteTaxWithholdingCorrection,
  { state: RootState }
>(
  "taxWithholdingCorrections/deleteTalentTaxWithholdingCorrection",
  async (
    taxWithholdingCorrection: DeleteTaxWithholdingCorrection,
    { requestId },
  ) => {
    return await deleteTaxWithholdingCorrection(taxWithholdingCorrection, {
      requestId,
    });
  },
);

const upsertState = (
  state: SliceState,
  events: EventPayload<TaxWithholdingCorrectionEvent>[],
) => {
  events.forEach((event) => {
    switch (event.name) {
      case "taxWthCorNew":
        const eventPayload = event.payload as TaxWithholdingCorrectionCreated;

        const newCorrection: TalentTaxWithholdingCorrection = {
          id: event.id,
          version: event.version,
          talentId: eventPayload.talentId,
          date: eventPayload.date,
          comment: eventPayload.comment,
          amount: eventPayload.amount,
          isLocked: false, // TODO: This could be returned with the event
        };

        taxWithholdingCorrectionsAdapter.addOne(state, newCorrection);

        break;
      case "taxWthDateChg": {
        const eventPayload =
          event.payload as TaxWithholdingCorrectionDateUpdated;

        const updates: Partial<TalentTaxWithholdingCorrection> = {
          date: eventPayload.date,
        };

        taxWithholdingCorrectionsAdapter.updateOne(state, {
          id: event.id,
          changes: updates,
        });

        break;
      }
      case "taxWthCmntChg": {
        const eventPayload = event.payload as TaxWithholdingCommentUpdated;

        const updates: Partial<TalentTaxWithholdingCorrection> = {
          comment: eventPayload.comment,
        };

        taxWithholdingCorrectionsAdapter.updateOne(state, {
          id: event.id,
          changes: updates,
        });

        break;
      }
      case "taxWthAmntChg": {
        const eventPayload =
          event.payload as TaxWithholdingCorrectionAmountUpdated;

        const updates: Partial<TalentTaxWithholdingCorrection> = {
          amount: eventPayload.amount,
        };

        taxWithholdingCorrectionsAdapter.updateOne(state, {
          id: event.id,
          changes: updates,
        });

        break;
      }
      case "taxWthDel": {
        taxWithholdingCorrectionsAdapter.removeOne(state, event.id);
      }
    }
  });
};

export const taxWithholdingCorrectionsSlice = createSlice({
  name: "taxWithholdingCorrections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Query
    builder.addCase(fetchTalentTaxWithholdingCorrections.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(
      fetchTalentTaxWithholdingCorrections.fulfilled,
      (state, action) => {
        state.status = "succeeded";
        taxWithholdingCorrectionsAdapter.setAll(state, action.payload);
      },
    );
    builder.addCase(fetchTalentTaxWithholdingCorrections.rejected, (state) => {
      state.status = "failed";
    });

    // Create
    builder.addCase(createTalentTaxWithholdingCorrection.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(
      createTalentTaxWithholdingCorrection.fulfilled,
      (state, action) => {
        upsertState(state, action.payload);
        state.status = "succeeded";
      },
    );
    builder.addCase(createTalentTaxWithholdingCorrection.rejected, (state) => {
      state.status = "failed";
    });

    // Update
    builder.addCase(updateTalentTaxWithholdingCorrection.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(
      updateTalentTaxWithholdingCorrection.fulfilled,
      (state, action) => {
        upsertState(state, action.payload);
        state.status = "succeeded";
      },
    );
    builder.addCase(updateTalentTaxWithholdingCorrection.rejected, (state) => {
      state.status = "failed";
    });

    // Delete
    builder.addCase(deleteTalentTaxWithholdingCorrection.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(
      deleteTalentTaxWithholdingCorrection.fulfilled,
      (state, action) => {
        upsertState(state, action.payload);
        state.status = "succeeded";
      },
    );
    builder.addCase(deleteTalentTaxWithholdingCorrection.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export const {
  selectAll: selectAllTaxWithholdingCorrections,
  selectById: selectTaxWithholdingCorrectionById,
} = taxWithholdingCorrectionsAdapter.getSelectors<RootState>(
  (state) => state.taxWithholdingCorrections,
);

export const taxWithholdingCorrectionsReducer =
  taxWithholdingCorrectionsSlice.reducer;
